// The single seam every outgoing analytics payload passes through.
//
// The tracker resolves `window[<data-before-send>]` at SEND time (not at
// load), awaits it, and treats a falsy return as "cancel this send" - see
// services/analytics.js, which puts the name on the tracker's script tag. That
// makes this the one place that can decide what leaves the browser, without a
// single call site having to know about any of it.
//
// Three jobs:
//
//   1. Drop admin traffic entirely. Staff clicking through the product is
//      noise in every feature-usage number this integration exists to produce,
//      and dropping it here is the only way it stays out of them.
//   2. Normalize the URL (see urls.js).
//   3. Repair two umami-kit defects that would otherwise pollute the data. See
//      services/vendor/README.md for the full list.

import { normalizePath } from './urls.js';

// The role verdict is a THREE-state, and the third state is what makes this
// work. The gate starts 'unknown': the app fires its first pageview well
// before POST /auth/verify has answered (see routes/protected.jsx), and a
// two-state flag would have to guess - either dropping a real visitor's first
// pageview, or sending an admin's.
//
// Instead, 'unknown' RETURNS A PROMISE. The tracker awaits beforeSend, so a
// payload raised before the verdict lands is simply held until it does, and
// then sent or dropped correctly. Nothing is lost and nothing is guessed.
let verdict = 'unknown'; // 'unknown' | 'allow' | 'suppress'
let waiters = [];

function settle(next) {
  verdict = next;
  const pending = waiters;
  waiters = [];
  pending.forEach((resolve) => resolve(next));
}

// Called by routes/protected.jsx once POST /auth/verify has answered, and by
// resolveVisitorAsAnonymous() below for anyone who never enters /app at all.
export function setVisitorRole(role) {
  settle(role === 'admin' ? 'suppress' : 'allow');
}

// A visitor on the public pages, the tour, or the auth screens is not an admin
// and never will be without a navigation into /app - so the gate opens rather
// than holding every landing-page event behind a call that is never made.
export function resolveVisitorAsAnonymous() {
  if (verdict === 'unknown') settle('allow');
}

// Only relevant to a signed-in visitor whose role we already know.
export function isSuppressed() {
  return verdict === 'suppress';
}

// umami-kit fires page-exit on EVERY visibilitychange -> hidden as well as on
// beforeunload, with no de-duplication of its own - so a visitor who switches
// tabs four times sends four page-exits for one page, each with a larger
// "total time" than the last. One per page is what the event is supposed to
// mean, so the extras are dropped here rather than being taught to mean
// something else.
let pageExitSent = false;
export function armPageExit() {
  pageExitSent = false;
}

export default async function beforeSend(type, payload) {
  if (verdict === 'suppress') return false;
  if (verdict === 'unknown') {
    const settled = await new Promise((resolve) => waiters.push(resolve));
    if (settled === 'suppress') return false;
  }

  if (payload?.name === 'page-exit') {
    if (pageExitSent) return false;
    pageExitSent = true;
  }

  // umami-kit computes idleDuration AFTER resetting lastActivity, so it always
  // reports ~0. Dropped rather than recorded: a field that is always zero
  // reads like a real measurement to whoever opens the dashboard next.
  if (payload?.name === 'user-active' && payload.data) {
    const { idleDuration, ...rest } = payload.data;
    payload = { ...payload, data: Object.keys(rest).length ? rest : undefined };
  }

  if (typeof payload?.url === 'string') {
    payload = { ...payload, url: normalizePath(payload.url) };
  }
  // referrer is a URL on this same origin whenever the visitor came from
  // another page here, so it needs the same treatment - an internal referrer
  // of /sign-in?user_hint=<email> would leak exactly what url no longer does.
  if (typeof payload?.referrer === 'string' && payload.referrer.startsWith('/')) {
    payload = { ...payload, referrer: normalizePath(payload.referrer) };
  }

  return payload;
}
